import { Box, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EngagementBarChartComponent } from './engagement-count-bar-chart.component';
import { EngagementLineChartComponent } from './engagement-count-line-chart.component';
import {
	AnalyticsEngagementsQueryParams,
	EngagementChartTypeArray,
	EngagementChartTypeEnum,
} from '@families-link/shared';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useGetAnalyticsEngagementsChartDataQuery } from '../../../../store/api/analytics.api';
import dayjs from 'dayjs';
import { FilterValuesStrings } from '../../pages/analytics.page';
import { FadeSpinner } from '../../../_core/components/_ui/fade-spinner';

export type EngagementFullDateChartProps = {
	transformedFilterValues: FilterValuesStrings;
	currentDistrictId?: string | null;
	authToken?: string;
};

export const EngagementFullDateChart: FC<EngagementFullDateChartProps> = (props) => {
	const { currentDistrictId, transformedFilterValues, authToken } = props;
	const [t] = useTranslation();
	const [currentChartType, setCurrentChartType] = useState(EngagementChartTypeEnum.TotalCount);
	const normalArgs: AnalyticsEngagementsQueryParams = {
		chartType: currentChartType,
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(transformedFilterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(transformedFilterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	const authenticatedNormalArgs = useMemo(
		() => ({
			queryParams: normalArgs,
			authToken,
		}),
		[normalArgs, authToken]
	);

	const normalQuery = useGetAnalyticsEngagementsChartDataQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const data = normalQuery.data;
	const isFetching = normalQuery.isFetching;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				gap: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
				<Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#797F92' }}>Engagements</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', ml: 'auto' }}>
					{EngagementChartTypeArray.map((value: EngagementChartTypeEnum) => (
						<CoreButton
							sx={
								currentChartType === value
									? {
											fontSize: '12px',
											p: 1,
											height: '24px',
											bgcolor: (theme) => theme.colors.lightBlue,
											color: (theme) => theme.colors.white,
											'&:hover': {
												bgcolor: (theme) => theme.colors.lightBlue,
												borderWidth: '1px',
											},
									  }
									: {
											fontSize: '12px',
											p: 1,
											height: '24px',
											'&:hover': {
												color: (theme) => theme.colors.lightBlue,
												borderWidth: '1px',
											},
									  }
							}
							variant="base"
							onClick={() => setCurrentChartType(value)}
						>
							{value}
						</CoreButton>
					))}
				</Box>
			</Box>
			{isFetching ? (
				<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
					<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
				</Box>
			) : (
				<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{data && data.length > 0 ? (
						currentChartType === EngagementChartTypeEnum.TotalCount ? (
							<EngagementBarChartComponent data={data} />
						) : (
							<EngagementLineChartComponent data={data} />
						)
					) : (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
								No data found for the chart
							</Typography>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};
